import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/use-current-user';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';

export default function ProvisioningFailedPage() {
  const { t } = useTranslation('common');
  const [, setLocation] = useLocation();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const organizationId = (user as any)?.organizationId as string | undefined;
  const provisioningError = (user as any)?.orgProvisioningError as string | null | undefined;

  const retryMutation = useMutation({
    mutationFn: async () => {
      if (!organizationId) {
        throw new Error('MISSING_ORGANIZATION_ID');
      }
      return apiClient.retryOrganizationProvisioning(organizationId);
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('provisioning.retryStarted'),
      });
      setLocation('/provisioning-pending');
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: t('provisioning.retryFailed'),
      });
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="card-container w-full max-w-lg p-8 text-center space-y-4">
        <h1 className="text-lg font-semibold text-[#333333]">
          {t('provisioning.failedTitle')}
        </h1>
        <p className="text-sm text-[#666666]">
          {t('provisioning.failedMessage')}
        </p>
        {provisioningError ? (
          <p className="text-xs text-[#666666] break-all">
            {provisioningError}
          </p>
        ) : null}
        <div className="flex justify-center">
          <Button
            onClick={() => retryMutation.mutate()}
            disabled={retryMutation.isPending || !organizationId}
          >
            {retryMutation.isPending
              ? t('provisioning.retrying')
              : t('provisioning.retry')}
          </Button>
        </div>
      </div>
    </div>
  );
}
